import { MuiTypography } from '@/features/shared/material-ui';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useWindowSize } from '@react-hook/window-size';
import clsx from 'clsx';
import { MasonryScroller, useContainerPosition, usePositioner, useResizeObserver } from 'masonic';
import React, { memo, useMemo, useRef } from 'react';
import ContentCard from '../shared/ContentCard';
import { StackedCardListProps } from './StackedCardList.interface';
import useStyles from './StackedCardList.styles';

const customContentCard =
  ({ classes, cardColor, cardTheme }: any) =>
  ({ data }: any) => {
    return data.isHeader ? (
      <>
        {data.listSuperheading && (
          <MuiTypography
            variant="overline"
            component="div"
            className={classes.superHeading}
            dangerouslySetInnerHTML={{ __html: data.listSuperheading }}
          />
        )}
        {data.listTitle && (
          <MuiTypography
            variant="h4"
            className={classes.title}
            dangerouslySetInnerHTML={{ __html: data.listTitle }}
          />
        )}
      </>
    ) : (
      <ContentCard data={data} cardColor={cardColor} theme={cardTheme} />
    );
  };

const StackedCardList = ({
  content,
  cardColor,
  theme,
  cardTheme,
  contentButtons,
}: Br.PropsWithContentButtons<StackedCardListProps>) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { listSuperheading, listTitle, contentList = [] } = content || {};
  const classes = useStyles({});
  const cards: any[] = useMemo(
    () =>
      contentList.map((c) => {
        return { ...c, isHeader: false };
      }),
    [contentList],
  );

  const containerRef = useRef(null);
  const [windowWidth, windowHeight] = useWindowSize();
  const { offset, width } = useContainerPosition(containerRef, [windowWidth, windowHeight]);

  const positioner = usePositioner(
    { width, columnCount: isMobile ? 1 : 2, columnGutter: isMobile ? 32 : 88 },
    [cards],
  );

  const resizeObserver = useResizeObserver(positioner);

  const CardWithTheme = useMemo(
    () =>
      customContentCard({
        classes,
        cardColor,
        cardTheme,
      }),
    [cardColor, cardTheme, classes],
  );

  const cardsWithHeader = useMemo(() => {
    if (listSuperheading || listTitle) {
      return [{ id: 'stacked_list_header', isHeader: true, listSuperheading, listTitle }, ...cards];
    }

    return cards;
  }, [cards, listSuperheading, listTitle]);

  const rootClassName = clsx(
    classes.root,
    cardsWithHeader.length > 0 && classes.wrapper,
    `${theme}-theme`,
  );

  return (
    <div className={rootClassName} key={content?.contentList?.length}>
      {contentButtons?.content}
      <MasonryScroller
        tabIndex={-1}
        positioner={positioner}
        resizeObserver={resizeObserver}
        containerRef={containerRef}
        overscanBy={cardsWithHeader.length}
        items={cardsWithHeader}
        height={windowHeight}
        offset={offset}
        render={CardWithTheme}
      />
    </div>
  );
};

export default memo(StackedCardList);
